import type { ApiClient } from '@/api/client.types';

import { earnedByUnit, type RawSectionStatLike } from './kaiCoachContent';
import type {
  RawCoachAssessment,
  RawKaiCoachConversation,
  RawKaiCoachMessage,
  RawMessageBlock,
} from './kaiCoachTypes';

// goalReached is Kudu's badge on the reply that earned a unit's goal credit.
export type CoachBlockType = 'markdown' | 'assignmentPrompt' | 'contextChip' | 'goalReached';

export interface CoachBlock {
  contextLabel?: string;
  markdown?: string;
  type: CoachBlockType;
}

// Kai's private verdict on an assistant message. Only instructors receive it
// (the detailed-scores export); a student's messages never carry one.
export interface CoachAssessment {
  evidence: string;
  pointsAwarded: number | null;
  reached: boolean;
}

export interface CoachMessage {
  assessment?: CoachAssessment;
  blocks: CoachBlock[];
  id: string;
  role: 'assistant' | 'user';
}

export interface CoachThreadKey {
  activityUid: string;
  courseUid: string;
  moduleUid: string;
  sessionUid: string;
  userId: string;
}

// A session, as opposed to a thread: the unit the backend prepares a shared
// context root for when the session is split into activities.
export interface CoachSessionKey {
  courseUid: string;
  moduleUid: string;
  sessionUid: string;
  userId: string;
}

export interface LoadConversationResult {
  // When the thread's goal was reached (goal-reached credit), or null.
  goalReachedAtMillis: number | null;
  injectedBlocks: CoachBlock[];
  messages: CoachMessage[];
}

interface RawGetConversationPayload {
  conversation?: RawKaiCoachConversation | null;
  injected_blocks?: RawMessageBlock[] | null;
  injectedBlocks?: RawMessageBlock[] | null;
}

interface RawPreparePayload {
  prepared?: boolean | null;
}

interface RawStudentComponentPayload {
  component?: {
    section_stats?: RawSectionStatLike[] | null;
    sectionStats?: RawSectionStatLike[] | null;
  } | null;
}

interface RawSendPayload {
  assistant_message?: RawKaiCoachMessage | null;
  assistantMessage?: RawKaiCoachMessage | null;
}

function unwrap<T>(response: unknown): T {
  if (response && typeof response === 'object' && 'Response' in response) {
    return (response as { Response: T }).Response;
  }
  return response as T;
}

function normalizeBlockType(type: unknown): CoachBlockType {
  if (type === 1 || type === 'ASSIGNMENT_PROMPT') {
    return 'assignmentPrompt';
  }
  if (type === 2 || type === 'CONTEXT_CHIP') {
    return 'contextChip';
  }
  if (type === 4 || type === 'GOAL_REACHED') {
    return 'goalReached';
  }
  return 'markdown';
}

function normalizeAssessment(raw: RawCoachAssessment | null | undefined): CoachAssessment | undefined {
  if (!raw || typeof raw.reached !== 'boolean') {
    return undefined;
  }
  const points = Number(raw.points_awarded ?? raw.pointsAwarded);
  return {
    evidence: typeof raw.evidence === 'string' ? raw.evidence.trim() : '',
    pointsAwarded:
      (raw.points_awarded ?? raw.pointsAwarded) != null && Number.isFinite(points) ? points : null,
    reached: raw.reached,
  };
}

function normalizeBlocks(blocks: RawMessageBlock[] | null | undefined): CoachBlock[] {
  return (blocks ?? []).map((block) => ({
    contextLabel:
      typeof (block.context_label ?? block.contextLabel) === 'string'
        ? (block.context_label ?? block.contextLabel) ?? undefined
        : undefined,
    markdown: typeof block.markdown === 'string' ? block.markdown : undefined,
    type: normalizeBlockType(block.type),
  }));
}

export function normalizeMessage(
  message: RawKaiCoachMessage,
  index: number,
): CoachMessage {
  const rawRole = message.role;
  const role: CoachMessage['role'] =
    rawRole === 1 || rawRole === 'ASSISTANT' ? 'assistant' : 'user';
  const id =
    (typeof message.uid === 'string' && message.uid) ||
    `coach-message-${index}`;
  const assessment = normalizeAssessment(message.assessment);
  return {
    ...(assessment ? { assessment } : {}),
    blocks: normalizeBlocks(message.blocks),
    id,
    role,
  };
}

export class KaiCoachService {
  constructor(private readonly apiClient: ApiClient) {}

  async loadConversation(key: CoachThreadKey): Promise<LoadConversationResult> {
    const response = await this.apiClient.invokeKuduMethod<
      RawGetConversationPayload | { Response: RawGetConversationPayload },
      {
        activity_uid: string;
        course_uid: string;
        module_uid: string;
        session_uid: string;
        user_id: string;
      }
    >('GetKaiCoachConversation', {
      activity_uid: key.activityUid,
      course_uid: key.courseUid,
      module_uid: key.moduleUid,
      session_uid: key.sessionUid,
      user_id: key.userId,
    });

    const payload = unwrap<RawGetConversationPayload>(response);
    const messages = (payload.conversation?.messages ?? []).map(normalizeMessage);
    const reachedAt = Number(
      payload.conversation?.goal_reached_at_utc_milli ??
        payload.conversation?.goalReachedAtUtcMilli ??
        '',
    );
    return {
      goalReachedAtMillis: Number.isFinite(reachedAt) && reachedAt > 0 ? reachedAt : null,
      injectedBlocks: normalizeBlocks(
        payload.injected_blocks ?? payload.injectedBlocks,
      ),
      messages,
    };
  }

  // Asks the backend to build the student's shared context root for a split
  // session, ahead of their first message. Fire-and-forget from the UI: the
  // send path never waits on it and falls back gracefully when it is absent.
  async prepareSession(key: CoachSessionKey): Promise<boolean> {
    const response = await this.apiClient.invokeKuduMethod<
      RawPreparePayload | { Response: RawPreparePayload },
      {
        course_uid: string;
        module_uid: string;
        session_uid: string;
        user_id: string;
      }
    >('PrepareKaiCoachSession', {
      course_uid: key.courseUid,
      module_uid: key.moduleUid,
      session_uid: key.sessionUid,
      user_id: key.userId,
    });
    return unwrap<RawPreparePayload>(response).prepared === true;
  }

  async sendMessage(
    key: CoachThreadKey,
    message: string,
    focusAreaUid = '',
  ): Promise<CoachMessage> {
    const response = await this.apiClient.invokeKuduMethod<
      RawSendPayload | { Response: RawSendPayload },
      {
        activity_uid: string;
        course_uid: string;
        focus_area_uid: string;
        message: string;
        module_uid: string;
        session_uid: string;
        user_id: string;
      }
    >('SendKaiCoachMessage', {
      activity_uid: key.activityUid,
      course_uid: key.courseUid,
      focus_area_uid: focusAreaUid,
      message,
      module_uid: key.moduleUid,
      session_uid: key.sessionUid,
      user_id: key.userId,
    });

    const payload = unwrap<RawSendPayload>(response);
    const assistant = payload.assistant_message ?? payload.assistantMessage;
    if (!assistant) {
      throw new Error('The coach reply was incomplete. Please try again.');
    }
    return normalizeMessage(assistant, 0);
  }

  // What a student has earned in each unit of a coach, from their own course
  // component's section stats (keyed by unit uid) — for telling which of the
  // coach's deadlines they have already met.
  async loadEarnedByUnit(key: {
    componentId: string;
    courseId: string;
    userId: string;
  }): Promise<Record<string, number>> {
    const response = await this.apiClient.invokeKuduMethod<
      RawStudentComponentPayload | { Response: RawStudentComponentPayload },
      { component_id: string; course_id: string; user_id: string }
    >('GetCourseStudentComponent', {
      component_id: key.componentId,
      course_id: key.courseId,
      user_id: key.userId,
    });
    const component = unwrap<RawStudentComponentPayload>(response).component;
    return earnedByUnit(component?.section_stats ?? component?.sectionStats);
  }
}
